import { XMLParser, XMLBuilder, XMLValidator } from "fast-xml-parser";
import {
  orderLookupCatalog,
  statementSelectionSchema,
  validateSubmission,
  effectiveFields,
  type Project,
  type Values,
  type Context,
  type Field,
} from "@proof/core";
export class XvpError extends Error {}
export type XvpValues = Record<string, string>;
export type OrderIdentifiers = Partial<
  Record<"ORDER_DISP_NO" | "CUSTOM_IDENTIFIER" | "PROOF_NAME", string>
>;
const namespace = "http://www.pageflex.com/schemas";
const validText = (value: string) =>
  !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF\uD800-\uDFFF]/u.test(
    value,
  );
type Node = Record<string, unknown>;
const tag = (node: Node) =>
  Object.keys(node).find(
    (key) => key !== ":@" && !key.startsWith("#") && !key.startsWith("?"),
  );
function bounded(xml: string) {
  if (
    typeof xml !== "string" ||
    new TextEncoder().encode(xml).length > 1000000 ||
    (xml.match(/</g)?.length || 0) > 5000
  )
    throw new XvpError("XVP_LIMIT");
  if (/<!\s*(DOCTYPE|ENTITY)/i.test(xml))
    throw new XvpError("XVP_DECLARATION_FORBIDDEN");
}
function read(xml: string) {
  bounded(xml);
  if (XMLValidator.validate(xml) !== true) throw new XvpError("XVP_INVALID");
  let nodes: Node[];
  try {
    nodes = new XMLParser({
      preserveOrder: true,
      ignoreAttributes: false,
      parseTagValue: false,
      trimValues: false,
      maxNestedTags: 4,
    }).parse(xml);
  } catch {
    throw new XvpError("XVP_INVALID");
  }
  const roots = nodes.filter((node) => tag(node)),
    root = roots[0],
    rootTag = root && tag(root)!;
  if (roots.length !== 1 || rootTag.split(":").at(-1) !== "doc_variables")
    throw new XvpError("XVP_ROOT_INVALID");
  const attrs = (root[":@"] || {}) as Record<string, string>;
  const checkNamespace = (name: string, local: Record<string, string> = {}) => {
    const prefix = name.includes(":") ? name.split(":")[0] : "";
    const uri =
      local[prefix ? `@_xmlns:${prefix}` : "@_xmlns"] ??
      attrs[prefix ? `@_xmlns:${prefix}` : "@_xmlns"];
    if (uri !== undefined && uri !== namespace)
      throw new XvpError("XVP_NAMESPACE_INVALID");
    if (prefix && uri === undefined && prefix !== "pfjob")
      throw new XvpError("XVP_NAMESPACE_INVALID");
  };
  checkNamespace(rootTag);
  const seen = new Set<string>(),
    entries: { node: Node; tag: string; name: string; value: string }[] = [];
  for (const node of root[rootTag] as Node[]) {
    const name = tag(node);
    if (!name) continue;
    if (name.split(":").at(-1) !== "var")
      throw new XvpError("XVP_NODE_UNSUPPORTED");
    const attributes = (node[":@"] || {}) as Record<string, string>,
      key = attributes["@_name"];
    checkNamespace(name, attributes);
    if (!key || key.length > 200 || seen.has(key))
      throw new XvpError("XVP_VARIABLE_INVALID");
    if (
      attributes["@_howToInterpret"] !== undefined &&
      attributes["@_howToInterpret"] !== "plain"
    )
      throw new XvpError("XVP_INTERPRETATION_UNSUPPORTED");
    const contents = node[name] as Node[];
    if (contents.some((child) => tag(child)))
      throw new XvpError("XVP_NESTED_VALUE");
    const value = contents
      .map((child) =>
        typeof child["#text"] === "string" ? child["#text"] : "",
      )
      .join("");
    if (value.length > 32768) throw new XvpError("XVP_VALUE_LIMIT");
    if (!validText(value) || !validText(key))
      throw new XvpError("XVP_TEXT_INVALID");
    seen.add(key);
    entries.push({ node, tag: name, name: key, value });
    if (entries.length > 1000) throw new XvpError("XVP_VARIABLE_LIMIT");
  }
  return { nodes, root, rootTag, entries };
}
export function parseXvp(xml: string): XvpValues {
  if (xml === "") return {};
  return Object.fromEntries(
    read(xml).entries.map((entry) => [entry.name, entry.value]),
  );
}
export function writeXvp(values: Readonly<XvpValues>): string {
  const entries = Object.entries(values);
  if (
    entries.length > 1000 ||
    entries.some(
      ([key, value]) =>
        !key ||
        key.length > 200 ||
        typeof value !== "string" ||
        value.length > 32768 ||
        !validText(key) ||
        !validText(value),
    )
  )
    throw new XvpError("XVP_VARIABLE_INVALID");
  const xml = new XMLBuilder({ ignoreAttributes: false }).build({
    "pfjob:doc_variables": {
      "@_xmlns:pfjob": namespace,
      "pfjob:var": entries.map(([key, value]) => ({
        "@_name": key,
        "@_howToInterpret": "plain",
        "#text": value,
      })),
    },
  });
  bounded(xml);
  return xml;
}
function transform(
  xml: string,
  replace: (key: string, value: string) => string,
) {
  if (!xml)
    return {
      xml,
      values: {},
      changed: [] as string[],
      unresolved: [] as string[],
    };
  const parsed = read(xml),
    values: XvpValues = Object.fromEntries(
      parsed.entries.map((entry) => [entry.name, entry.value]),
    ),
    changed: string[] = [];
  for (const entry of parsed.entries) {
    const value = replace(entry.name, entry.value);
    values[entry.name] = value;
    if (value !== entry.value) {
      entry.node[entry.tag] = [{ "#text": value }];
      changed.push(entry.name);
    }
  }
  let output = xml;
  if (changed.length) {
    const attrs = (parsed.root[":@"] ||= {}) as Record<string, string>;
    if (
      (parsed.rootTag.startsWith("pfjob:") ||
        parsed.entries.some((e) => e.tag.startsWith("pfjob:"))) &&
      !attrs["@_xmlns:pfjob"]
    )
      attrs["@_xmlns:pfjob"] = namespace;
    output = new XMLBuilder({
      preserveOrder: true,
      ignoreAttributes: false,
    }).build(parsed.nodes);
    bounded(output);
  }
  return {
    xml: output,
    values,
    changed,
    unresolved: Object.entries(values)
      .filter(([, value]) =>
        orderLookupCatalog.some((item) => value.includes(item.placeholder)),
      )
      .map(([key]) => key),
  };
}
export function finalizeOrderXvp(xml: string, identifiers: OrderIdentifiers) {
  if (
    Object.entries(identifiers).some(
      ([key, value]) =>
        !orderLookupCatalog.some((item) => item.key === key) ||
        typeof value !== "string" ||
        value.length > 32768 ||
        !validText(value),
    )
  )
    throw new XvpError("ORDER_IDENTIFIERS_INVALID");
  return transform(xml, (key, value) => {
    const item = orderLookupCatalog.find(
      (item) =>
        key === item.key ||
        key === item.placeholder ||
        value === item.placeholder,
    );
    return item && Object.hasOwn(identifiers, item.key)
      ? identifiers[item.key as keyof OrderIdentifiers]!
      : value;
  });
}
/** Reset uses explicit bindings or canonical names; unrelated equal-valued text is preserved. */
export function clearOrderXvp(
  xml: string,
  bindings: Readonly<Record<string, keyof OrderIdentifiers>> = {},
) {
  if (
    Object.values(bindings).some(
      (key) => !orderLookupCatalog.some((item) => item.key === key),
    )
  )
    throw new XvpError("ORDER_BINDINGS_INVALID");
  return transform(
    xml,
    (key, value) =>
      orderLookupCatalog.find(
        (item) =>
          key === item.key ||
          key === item.placeholder ||
          value === item.placeholder ||
          (Object.hasOwn(bindings, key) && bindings[key] === item.key),
      )?.placeholder || value,
  );
}
export function orderXvpBindings(
  project: Project,
  partId?: string,
): Record<string, keyof OrderIdentifiers> {
  return Object.fromEntries(
    effectiveFields(project, partId)
      .filter(
        (field) =>
          field.key &&
          field.source.kind === "order" &&
          orderLookupCatalog.some(
            (item) => item.key === (field.source.key || field.key),
          ),
      )
      .map((field) => [
        field.key,
        (field.source.key || field.key) as keyof OrderIdentifiers,
      ]),
  );
}
export function finalizeOrderFile<T extends Record<string, unknown>>(
  row: T,
  identifiers: OrderIdentifiers,
) {
  if (typeof row.PAGEFLEX_XVP_XML !== "string")
    throw new XvpError("ORDER_FILE_XVP_MISSING");
  const result = finalizeOrderXvp(row.PAGEFLEX_XVP_XML, identifiers);
  return {
    ...result,
    ready: result.unresolved.length === 0,
    row: structuredClone({
      ...row,
      ...(result.changed.length
        ? { PAGEFLEX_XVP_XML: result.xml, SP_LOW_RES_PDF: "" }
        : {}),
    }),
  };
}
export function projectToXvp(
  project: Project,
  values: Values,
  partId?: string,
  context?: Context,
  formatter?: (field: Field, value: Values[string]) => string,
) {
  const checked = validateSubmission(project, values, partId, context);
  if (checked.errors.length) throw new XvpError("XVP_SUBMISSION_INVALID");
  const result: [string, string][] = [];
  for (const field of effectiveFields(project, partId)) {
    if (!field.key || !Object.hasOwn(checked.snapshot, field.id)) continue;
    const value = checked.snapshot[field.id];
    const flat = formatter
      ? formatter(field, value)
      : value === null || value === undefined
        ? ""
        : statementSelectionSchema.safeParse(value).success
          ? statementSelectionSchema.parse(value).text
          : ["image", "map"].includes(field.type) &&
              typeof value === "object" &&
              !Array.isArray(value)
            ? String(value.assetId || "")
            : typeof value === "object"
              ? JSON.stringify(value)
              : String(value);
    result.push([field.key, flat]);
  }
  return writeXvp(Object.fromEntries(result));
}
